import { apiGet, apiPost } from '../../../adapters/api-client/api';

/**
 * PI-3 Epic 3 — admin/support API client.
 *
 * All endpoints sit under /api/v1/admin/* and require ADMIN, MODERATOR, or
 * SYSTEM role; the refund-approval endpoints additionally require ADMIN
 * (not MODERATOR) per the two-admin gate.
 */

// ── Shared types ─────────────────────────────────────────────────────────────

export type RoutingHint = 'auto_resolvable' | 'semimanual' | 'escalate';
export type SupportCaseKind = 'refund_approval' | 'open_dispute' | 'possible_no_show' | 'generic';

export interface RoutingDecision {
  hint: RoutingHint;
  reason: string;
  slaBreached: boolean;
}

export interface SupportQueueRow {
  caseId: string;
  caseKind: SupportCaseKind;
  enrollmentId: string;
  openedAt: string;
  elapsedHours: number;
  moneyAtStakeUsd: number;
  slaHours: number;
  /** The governed due-soon window — DEC-A02-07. */
  dueSoonHours: number;
  routing: RoutingDecision;
  summary: string;
}

// ── S3.1 — case view ─────────────────────────────────────────────────────────

export interface EnrollmentSummary {
  enrollmentId: string;
  parentId: string;
  childId: string;
  teacherId: string;
  courseId: string;
  state: string;
  total: number;
  /** What every amount on this enrollment is denominated in. */
  currency: string;
  bookingFeeAmount: number;
  perSessionAmount: number;
  holdbackAmount: number;
  bookingFeeReleased: boolean;
  holdbackReleased: boolean;
  refundedAmount: number;
  enrollmentDate: string;
  activatedAt?: string;
  cancelledAt?: string;
}

export interface MilestoneSummary {
  milestoneId: string;
  sequence: number;
  type: string;
  status: string;
  amount: number;
  completedAt?: string;
  releasedAt?: string;
  releaseId?: string;
  disputeId?: string;
}

export interface BookingSummary {
  bookingId: string;
  milestoneSequence: number;
  state: string;
  scheduledStart?: string;
  scheduledEnd?: string;
  proposedStart?: string;
  proposedEnd?: string;
  cancellationReason?: string;
}

export interface PaymentOpSnapshot {
  operationKey: string;
  action: 'release_booking' | 'release_session' | 'release_holdback' | 'refund_cancellation';
  success: boolean;
  id?: string;
  failureMessage?: string;
  cached: boolean;
}

export interface NotificationSnapshot {
  notificationId: string;
  kind: string;
  recipients: string[];
  dispatched: boolean;
  attempts: number;
  dispatchedAt?: string;
  lastError?: string;
}

export interface SupportAction {
  actionId: string;
  actionType: 'RESCHEDULE' | 'CANCELLATION' | 'REFUND' | 'APPROVAL' | 'COMM';
  status: 'LOGGED' | 'PENDING_APPROVAL' | 'APPROVED' | 'REJECTED' | 'FAILED';
  enrollmentId: string;
  actingAdminId: string;
  reason: string;
  amount?: number;
  refundId?: string;
  operationKey?: string;
  createdAt: string;
  updatedAt: string;
  // Refund / approval extras
  milestoneId?: string;
  supportingNotes?: string;
  approvedBy?: string;
  approvedAt?: string;
  rejectedBy?: string;
  rejectedAt?: string;
  rejectionReason?: string;
  // Reschedule extras
  bookingId?: string;
  originalStart?: string;
  originalEnd?: string;
  newStart?: string;
  newEnd?: string;
  // Cancellation extras
  refundToParent?: number;
  penaltyWaived?: boolean;
}

export interface SupportCasePayload {
  enrollment: EnrollmentSummary;
  milestones: MilestoneSummary[];
  bookings: BookingSummary[];
  disputes: unknown[];
  recentPayments: PaymentOpSnapshot[];
  recentNotifications: NotificationSnapshot[];
  supportActions: SupportAction[];
}

// ── S3.3 — cancellation list ─────────────────────────────────────────────────

export interface CancellationListRow {
  enrollmentId: string;
  parentId: string;
  teacherId: string;
  cancelledAt: string;
  cancellationReason?: string;
  refundedAmount: number;
  overrideActionId?: string;
  overrideAdminId?: string;
  overrideReason?: string;
  penaltyWaived?: boolean;
}

// ── API surface ─────────────────────────────────────────────────────────────

export function getSupportCase(enrollmentId: string): Promise<SupportCasePayload> {
  return apiGet(`/api/v1/admin/support/enrollments/${enrollmentId}`);
}

export function getSupportQueue(): Promise<{ rows: SupportQueueRow[] }> {
  return apiGet('/api/v1/admin/support/queue');
}

export function adminReschedule(
  bookingId: string,
  body: { newStart: string; newEnd: string; reason: string },
): Promise<{ booking: unknown; action: SupportAction }> {
  return apiPost(`/api/v1/admin/bookings/${bookingId}/reschedule`, body);
}

export function listCancellations(params: {
  from?: string;
  to?: string;
}): Promise<{ cancellations: CancellationListRow[] }> {
  const q = new URLSearchParams();
  if (params.from) q.set('from', params.from);
  if (params.to) q.set('to', params.to);
  const suffix = q.toString() ? `?${q.toString()}` : '';
  return apiGet(`/api/v1/admin/cancellations${suffix}`);
}

export function cancelWithOverride(
  enrollmentId: string,
  body: { forcedRefundAmount: number; reason: string; penaltyWaived: boolean },
): Promise<{ enrollment: unknown; action: SupportAction }> {
  return apiPost(`/api/v1/admin/enrollments/${enrollmentId}/cancel-with-override`, body);
}

export function requestRefund(body: {
  enrollmentId: string;
  milestoneId?: string;
  amount: number;
  reason: string;
  supportingNotes?: string;
}): Promise<{ approval: SupportAction }> {
  return apiPost('/api/v1/admin/refunds', body);
}

export function approveRefund(
  actionId: string,
): Promise<{ approval: SupportAction; refund: SupportAction }> {
  return apiPost(`/api/v1/admin/refunds/${actionId}/approve`, {});
}

export function rejectRefund(
  actionId: string,
  rejectionReason: string,
): Promise<{ approval: SupportAction }> {
  return apiPost(`/api/v1/admin/refunds/${actionId}/reject`, { rejectionReason });
}
